import { useCallback, useEffect, useRef, useState } from "react";

export const ENABLE_TYPING_PREVIEW = true;

export const TYPING_PREVIEW = {
  startDelayMs: 90,
  tickMs: 45,
  tokensPerTick: 2,
  beforeBlockMs: 260,
  blockDwellMs: 420,
} as const;

export type MarkdownSegment =
  | { kind: "prose"; text: string; tokens: string[] }
  | { kind: "block"; text: string };

const TABLE_SEP = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const FENCE = /^```/;

export function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t.length > 0);
}

export function joinTokens(tokens: string[], count: number): string {
  return tokens.slice(0, Math.max(0, count)).join("");
}

export function segmentMarkdown(content: string): MarkdownSegment[] {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const segments: MarkdownSegment[] = [];
  let prose: string[] = [];

  const flushProse = () => {
    if (!prose.length) return;
    const text = prose.join("\n");
    segments.push({ kind: "prose", text, tokens: tokenize(text) });
    prose = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (FENCE.test(line.trim())) {
      flushProse();
      const buf = [line];
      i++;
      while (i < lines.length && !FENCE.test(lines[i].trim())) {
        buf.push(lines[i]);
        i++;
      }
      if (i < lines.length) {
        buf.push(lines[i]);
        i++;
      }
      segments.push({ kind: "block", text: buf.join("\n") });
      continue;
    }

    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      flushProse();
      const buf = [line, lines[i + 1]];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      segments.push({ kind: "block", text: buf.join("\n") });
      continue;
    }

    prose.push(line);
    i++;
  }
  flushProse();
  return segments;
}

export interface RevealPos {
  seg: number;
  tok: number;
}

export interface SegmentReveal {
  pos: RevealPos;
  done: boolean;
  skip: () => void;
}

export function useSegmentReveal(segments: MarkdownSegment[], run: boolean): SegmentReveal {
  const end = useCallback((): RevealPos => ({ seg: segments.length, tok: 0 }), [segments.length]);
  const [pos, setPos] = useState<RevealPos>(run ? { seg: 0, tok: 0 } : end());
  const [done, setDone] = useState(!run);
  const posRef = useRef<RevealPos>(pos);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  posRef.current = pos;

  const finish = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const e = { seg: segments.length, tok: 0 };
    posRef.current = e;
    setPos(e);
    setDone(true);
  }, [segments.length]);

  useEffect(() => {
    if (!run || segments.length === 0) {
      const e = { seg: segments.length, tok: 0 };
      posRef.current = e;
      setPos(e);
      setDone(true);
      return;
    }

    setDone(false);
    const start = { seg: 0, tok: 0 };
    posRef.current = start;
    setPos(start);
    let cancelled = false;

    const advance = () => {
      if (cancelled) return;
      const cur = posRef.current;
      const seg = segments[cur.seg];
      if (!seg) {
        setDone(true);
        return;
      }

      let next: RevealPos;
      if (seg.kind === "prose" && cur.tok < seg.tokens.length) {
        next = { seg: cur.seg, tok: Math.min(cur.tok + TYPING_PREVIEW.tokensPerTick, seg.tokens.length) };
      } else {
        const ni = cur.seg + 1;
        const nextSeg = segments[ni];
        const tok =
          nextSeg && nextSeg.kind === "prose"
            ? Math.min(TYPING_PREVIEW.tokensPerTick, nextSeg.tokens.length)
            : 0;
        next = { seg: ni, tok };
      }

      posRef.current = next;
      setPos(next);

      if (next.seg >= segments.length) {
        setDone(true);
        return;
      }

      const upcoming = segments[next.seg];
      let wait: number = TYPING_PREVIEW.tickMs;
      if (upcoming.kind === "block") {
        wait = TYPING_PREVIEW.beforeBlockMs;
      } else if (seg.kind === "block") {
        wait = TYPING_PREVIEW.blockDwellMs;
      }
      timerRef.current = setTimeout(advance, wait);
    };

    timerRef.current = setTimeout(advance, TYPING_PREVIEW.startDelayMs);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [run, segments]);

  return { pos, done, skip: finish };
}
